import React, { useCallback, useContext, useEffect, useMemo } from "react";
import _ from 'lodash';
import cx from "classnames";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import { invalidateCollectionUpdateState, toggleCollectionLive } from "src/redux/actions/collections";
import { Collection } from "src/types/collections";
import dateFormat from "dateformat";

import styles from './live.module.scss';
import Slider from "src/components/slider";
import { Checkbox } from "src/components/input";
import Button from "src/components/button";
import pluralize from "pluralize";
import { animated, useTransition } from '@react-spring/web';
import RichTextEditor from "src/components/richTextEditor";
import InfiniteScroll from "src/components/infiniteScroll";
import { CollectionContext } from "../context";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";

interface LiveConfirmationDialogProps {
  show: boolean;
  collection: Collection;
  onClose: () => void;
}

export const LiveConfirmationDialog: React.FC<LiveConfirmationDialogProps> = ({ show, collection, onClose }) => {
  const dispatch = useDispatch();
  
  const { trackLiveCollectionToggled, trackLiveCollectionScreenView } = useCollectionsAdoption();

  const live = useMemo(() => collection.live, [show, collection]);
  const recipientCount = collection.sharedRecipientsCount || 0;
  const updating = useSelector((state: any) => _.get(state, `collections.update['${collection.id}'].updating`, false));
  const updated = useSelector((state: any) => _.get(state, `collections.update['${collection.id}'].updated`, false));

  const [notifyRecipients, setNotifyRecipients] = React.useState(true);
  const [showRecipients, setShowRecipients] = React.useState(false);
  const [showCompose, setShowCompose] = React.useState(false);

  const [subject, setSubject] = React.useState(`${collection.name} was updated`);
  const [message, setMessage] = React.useState('');

  const liveTransition = useTransition(!showCompose, {
    initial: { opacity: 1, transform: 'translateX(0%)' },
    from: { opacity: 0, transform: 'translateX(-100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(-100%)' },
  });

  const composeTransition = useTransition(showCompose, {
    initial: { opacity: 0, transform: 'translateX(100%)' },
    from: { opacity: 0, transform: 'translateX(100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(100%)' },
  });

  useEffect(() => {
    setNotifyRecipients(true);
    setShowRecipients(false);
    setShowCompose(false);

    if(show){
      trackLiveCollectionScreenView();
    }
  }, [show]);

  useEffect(() => {
    if (show && updated) {
      trackLiveCollectionToggled({
        collection: collection,
        live: live || false,
        notifyRecipients: notifyRecipients,
      });

      dispatch(invalidateCollectionUpdateState());
      onClose();
    }
  }, [show, updated]);

  const handleNotifyRecipientsChange = () => {
    setNotifyRecipients(prev => !prev);
  }

  const handleViewRecipients = (e) => {
    e.preventDefault();
    setShowRecipients(true);
  }

  const handleConfirm = useCallback(() => {
    if (!live && recipientCount > 0 && notifyRecipients && !showCompose) {
      setShowCompose(true);
    } else {
      dispatch(toggleCollectionLive({
        id: collection.id as string,
        value: !live,
        notifyRecipients: notifyRecipients,
        subject: subject,
        message: message
      }));
    }
  }, [live, recipientCount, subject, message, notifyRecipients, showCompose]);

  return (
    <Slider.Dialog show={show} width={showRecipients ? '699px' : '400px'} className={cx(styles.confrimationDialog, styles.live)} onClose={onClose}>
      <Slider.Body className={styles.body}>
        <div className={styles.container}>
          <div className={styles.stacks}>
            {liveTransition((style, item) => item &&
              <LiveConfirmation
                collection={collection}
                notifyRecipients={notifyRecipients}
                style={style}
                onChange={handleNotifyRecipientsChange}
                onViewRecipients={handleViewRecipients}
              />
            )}
            {composeTransition((style, item) => item &&
              <ComposePanel style={style}
                collection={collection}
                subject={subject}
                message={message}
                onViewRecipients={handleViewRecipients}
                onChange={(subject, message) => {
                  setSubject(subject);
                  setMessage(message);
                }}
              />
            )}
          </div>
          <RecipientPanel collection={collection} show={showRecipients} onClose={() => setShowRecipients(false)} />
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary"
          icon={showCompose ? 'paperflite-send' : 'paperflite-tick'}
          text={updating ? 'Updating...' : showCompose ? 'Send' : 'Confirm'}
          disabled={updating}
          onClick={handleConfirm}
        />
        <Button text="Cancel" disabled={updating} onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface LiveConfirmationProps {
  collection: Collection;
  notifyRecipients: boolean;
  style: any;
  // eslint-disable-next-line no-unused-vars
  onViewRecipients: (e: any) => void;
  // eslint-disable-next-line no-unused-vars
  onChange: (notifyRecipients: boolean) => void;
}

const LiveConfirmation: React.FC<LiveConfirmationProps> = ({ collection, notifyRecipients, style, onChange, onViewRecipients }) => {
  const live = useMemo(() => collection.live, []);
  const recipientCount = collection.sharedRecipientsCount || 0;

  return (
    <animated.div style={style} className={styles.liveContainer}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          {live ? <>turn OFF<br /> live collection</> : <>turn ON<br /> live collection</>}
        </h2>
      </div>

      {!live &&
        <p className={styles.message}>
          Flip this switch to keep your collection live and updated, even after sharing with recipients
        </p>
      }

      {live &&
        <p className={styles.message}>
          Turning this feature OFF will stop syncing any further changes
          to this collection for recipients you may have shared with.
        </p>
      }

      {recipientCount == 0 &&
        <p className={cx(styles.message, styles.meta)}>
          This collection was created on {dateFormat(collection.createdDate, 'dd mmm yyyy')}.
          This collection wasn&apos;t shared with anyone.
        </p>
      }

      {recipientCount > 0 &&
        <p className={cx(styles.message, styles.meta)}>
          This collection was created on {dateFormat(collection.createdDate, 'dd mmm yyyy')}
          and has been shared with &nbsp;
          <a href="#" onClick={onViewRecipients}>
            {recipientCount} {pluralize("Recipient", recipientCount)}
          </a>.
        </p>
      }

      {!live && recipientCount > 0 &&
        <Checkbox
          className={styles.notifyRecipients}
          label="Notify Recipients"
          checked={notifyRecipients}
          onChange={onChange}
        />
      }
    </animated.div>
  )
}

interface ComposePanelProps {
  collection: Collection;
  subject: string;
  message: string;
  style: any;

  // eslint-disable-next-line no-unused-vars
  onViewRecipients: (e: any) => void;

  // eslint-disable-next-line no-unused-vars
  onChange: (subject: string, message: string) => void;
}

const ComposePanel: React.FC<ComposePanelProps> = ({ collection, style, message, subject, onChange, onViewRecipients }) => {
  const user = useSelector((state: any) => _.get(state, 'users.me'));
  const collectionContext = useContext(CollectionContext);

  const { recipients, totalElements } = useSelector((state: any) => {
    const recipientState = _.get(state, `${collectionContext.recipientSelector}`);
    const recipients = _.get(recipientState, 'items') || [];
    const loading = _.get(recipientState, 'loading');
    const pagination = _.get(recipientState, 'pagination');
    const totalElements = _.get(pagination, "totalRecordsCount", 0);

    return {
      loading,
      recipients,
      totalElements,
    }
  });

  const defaultMessage = useMemo(() => {
    return collectionContext.getNotificationContent().message;
  }, [collection]);

  const handleSubjectChange = (e) => {
    onChange(e.target.value, message);
  }

  const handleMessageChange = (content) => {
    onChange(subject, content.target.getContent());
  }

  const handleViewRecipients = (e) => {
    e.preventDefault();
    onViewRecipients(e);
  }

  useEffect(() => {
    onChange(subject, defaultMessage);
  }, [defaultMessage]);

  return (
    <animated.div style={style} className={styles.composeContainer}>
      <div className={styles.header}>
        <h2 className={styles.title}>live collection</h2>
        <p className={styles.message}>compose the mail here.</p>
      </div>
      <div className={cx(styles.field)}>
        <span className={styles.label}>To</span>
        <div className={styles.recipients}>
          {_.slice<{ firstName: string, lastName: string }>(recipients, 0, 2).map((recipient, index) => {
            const contact = collectionContext.getRecipient(recipient);
            return (
              <div key={index} className={styles.recipient}>
                <div className={styles.name}>{`${contact?.firstName || ''} ${contact?.lastName || ''}`}</div>
              </div>
            )
          })}
          {totalElements > 3 &&
            <a href="#" onClick={handleViewRecipients}>+{totalElements - 3} more</a>
          }
        </div>
      </div>
      <div className={cx(styles.field, styles.subject)}>
        <span className={styles.label}>Subject</span>
        <input placeholder="Enter subject" value={subject} onChange={handleSubjectChange} />
      </div>

      <RichTextEditor inline={true}
        className={styles.emailBody}
        toolbar='bold italic | alignleft aligncenter alignright | fontselect | fontsizeselect | forecolor backcolor'
        content={defaultMessage}
        onChange={handleMessageChange} />

    </animated.div>
  );
}

interface RecipientPanelProps {
  collection: Collection;
  show: boolean;

  // eslint-disable-next-line no-unused-vars
  onClose: () => void;
}

const RecipientPanel: React.FC<RecipientPanelProps> = ({ collection, show, onClose }) => {
  const collectionContext = useContext(CollectionContext);

  const { loading, recipients, totalElements, hasMoreItems, nextPage } = useSelector((state: any) => {
    const recipientState = _.get(state, `${collectionContext.recipientSelector}`);
    const recipients = _.get(recipientState, 'items') || [];
    const loading = _.get(recipientState, 'loading');
    const pagination = _.get(recipientState, 'pagination');
    const totalPages = _.get(pagination, "totalPages", 0);
    const totalElements = _.get(pagination, "totalRecordsCount", 0);
    const hasMoreItems = _.get(pagination, "hasMoreItems");

    let nextPage = _.get(pagination, "currentPage", 0) + 1;
    if (nextPage >= totalPages) {
      nextPage = null;
    }

    return {
      loading,
      recipients,
      totalElements,
      hasMoreItems,
      nextPage
    }
  }, _.isEqual);

  const recipientTransition = useTransition(show, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  useEffect(() => {
    fetchRecipientsForPage(0);
  }, [collection]);

  const fetchRecipientsForPage = (page: number) => {
    collectionContext.fetchRecipientsForPage(page);
  }

  const handleLoadRecipients = () => {
    if (nextPage) {
      fetchRecipientsForPage(nextPage);
    }
  }

  return (
    <>
      {recipientTransition((style, item) => item &&
        <animated.div style={style} className={styles.recipientsContainer}>
          <h3 className={styles.recipientsTitle}>
            prior recipients {totalElements > 0 && `(${totalElements})`}
            <button className={styles.close} onClick={onClose}>
              <i className="paperflite-close"></i>
            </button>
          </h3>

          <InfiniteScroll className={styles.recipients}
            loading={loading}
            hasMore={hasMoreItems}
            loadMore={handleLoadRecipients}
            loader={() => {
              return _.times(_.size(recipients) == 0 ? 4 : 1).map((i) => (
                <div key={i} className={cx(styles.recipient, styles.placeholder)}>
                  <div className={cx(styles.name, styles.block)}></div>
                  <div className={styles.details}>
                    <span className={cx(styles.company, styles.block)}></span>
                    <span className={cx(styles.sharedDate, styles.block)}></span>
                  </div>
                </div>
              ));
            }}
          >

            {recipients.map((recipient, index) => {
              const contact = collectionContext.getRecipient(recipient);
              return (
                <div key={index} className={styles.recipient}>
                  <div className={styles.name}>{`${contact?.firstName || ''} ${contact?.lastName || ''}`}</div>
                  <div className={styles.details}>
                    <span className={styles.company}>{contact?.company}</span>
                    <span className={styles.sharedDate}>{dateFormat(contact?.sharedDate, 'dd mmm yyyy')}</span>
                  </div>
                </div>
              );
            })}
          </InfiniteScroll>
        </animated.div>
      )}
    </>
  );
}

interface NotifyChangesDialogProps {
  show: boolean;
  collection: Collection;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (notifyRecipients: boolean, subject: string, message: string) => void;
  onClose: () => void;
}


export const NotifyChangesDialog: React.FC<NotifyChangesDialogProps> = ({ show, collection, onConfirm, onClose }) => {
  const dispatch = useDispatch();
  const live = useMemo(() => collection.live, []);

  const collectionContext = useContext(CollectionContext);
  const recipientCount = useSelector((state: any) => {
    const pagination = _.get(state, `${collectionContext.recipientSelector}`);
    const totalElements = _.get(pagination, "totalRecordsCount", 0);

    return totalElements
  }, _.isEqual);

  const updating = useSelector((state: any) => _.get(state, `${collectionContext.updateSelector}.updating`, false));
  const updated = useSelector((state: any) => _.get(state, `${collectionContext.updateSelector}.updated`, false));

  const [notifyRecipients, setNotifyRecipients] = React.useState(true);
  const [showRecipients, setShowRecipients] = React.useState(false);
  const [showCompose, setShowCompose] = React.useState(false);

  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');

  const liveTransition = useTransition(!showCompose, {
    initial: { opacity: 1, transform: 'translateX(0%)' },
    from: { opacity: 0, transform: 'translateX(-100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(-100%)' },
  });

  const composeTransition = useTransition(showCompose, {
    initial: { opacity: 0, transform: 'translateX(100%)' },
    from: { opacity: 0, transform: 'translateX(100%)' },
    enter: { opacity: 1, transform: 'translateX(0%)' },
    leave: { opacity: 0, transform: 'translateX(100%)' },
  });


  useEffect(() => {
    if (updated) {
      dispatch(invalidateCollectionUpdateState());
      onClose();
    }
  }, [updated]);

  useEffect(() => {
    setNotifyRecipients(true);
    setShowRecipients(false);
    setShowCompose(false);

    const emailContent = collectionContext.getNotificationContent();
    setSubject(emailContent.subject);
    setMessage(emailContent.message);
  }, [show]);

  const handleNotifyRecipientsChange = () => {
    setNotifyRecipients(prev => !prev);
  }

  const handleViewRecipients = (e) => {
    e.preventDefault();
    setShowRecipients(true);
  }

  const handleConfirm = useCallback(() => {
    if (notifyRecipients && !showCompose) {
      setShowCompose(true);
    } else {
      onConfirm(notifyRecipients, subject, message);
    }
  }, [live, recipientCount, subject, message, notifyRecipients, showCompose]);

  return (
    <Slider.Dialog show={show} width={showRecipients ? '699px' : '400px'} className={cx(styles.confrimationDialog, styles.live)} onClose={onClose}>
      <Slider.Body className={styles.body}>
        <div className={styles.container}>
          <div className={styles.stacks}>
            {liveTransition((style, item) => item &&
              <NotifyRecipients
                collection={collection}
                notifyRecipients={notifyRecipients}
                style={style}
                onChange={handleNotifyRecipientsChange}
                onViewRecipients={handleViewRecipients}
              />
            )}
            {composeTransition((style, item) => item &&
              <ComposePanel style={style}
                collection={collection}
                subject={subject}
                message={message}
                onViewRecipients={handleViewRecipients}
                onChange={(subject, message) => {
                  setSubject(subject);
                  setMessage(message);
                }}
              />
            )}
          </div>
          <RecipientPanel collection={collection} show={showRecipients} onClose={() => setShowRecipients(false)} />
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button variant="primary"
          icon={showCompose ? 'paperflite-send' : 'paperflite-tick'}
          text={updating ? 'Updating...' : showCompose ? 'Send' : 'Confirm'}
          disabled={updating}
          onClick={handleConfirm}
        />
        <Button text="Cancel" disabled={updating} onClick={onClose} />
      </Slider.Actions>
    </Slider.Dialog>
  );
}

interface NotifyRecipientsProps {
  collection: Collection;
  notifyRecipients: boolean;
  style: any;
  // eslint-disable-next-line no-unused-vars
  onViewRecipients: (e: any) => void;
  // eslint-disable-next-line no-unused-vars
  onChange: (notifyRecipients: boolean) => void;
}

const NotifyRecipients: React.FC<NotifyRecipientsProps> = ({ collection, notifyRecipients, style, onChange, onViewRecipients }) => {
  const collectionContext = useContext(CollectionContext);
  const entityType = collectionContext.entityType;

  const recipientCount = useSelector((state: any) => {
    const pagination = _.get(state, `${collectionContext.recipientSelector}.pagination`);
    const totalElements = _.get(pagination, "totalRecordsCount", 0);
    return totalElements
  }, _.isEqual);

  return (
    <animated.div style={style} className={styles.liveContainer}>
      <div className={styles.header}>
        <h2 className={styles.title}>Notify Recipients</h2>
      </div>

      {entityType === 'collection' &&
        <p className={styles.message}>
          This is a live collection.
          This feature keeps your collection live and in sync with all
          updates even after you've shared with recipients.
        </p>
      }

      <p className={cx(styles.message, styles.meta)}>
        This collection was created on {dateFormat(collection.createdDate, 'dd mmm yyyy')} &nbsp;
        and has been shared with &nbsp;
        <a href="#" onClick={onViewRecipients}>
          {recipientCount} {pluralize("Recipient", recipientCount)}
        </a>.
      </p>

      <p className={cx(styles.message, styles.meta)}>
        Would you like to notify the recipients of this collection about the changes you recently made?
      </p>

      <Checkbox
        className={styles.notifyRecipients}
        label="Notify Recipients"
        checked={notifyRecipients}
        onChange={onChange}
      />

    </animated.div>
  )
}